"use client";

import * as React from "react";
import type { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import AutoplayPlugin from "embla-carousel-autoplay";
import FadePlugin from "embla-carousel-fade";
import { cn } from "@marketing-sites/utils/cn";
import { CarouselContext, SlideContext, useCarousel } from "./carousel.context";
import { useWarmIndices } from "./carousel.hooks";
import type { RootProps, SlideProps } from "./carousel.types";

/** Stable empty set used while a carousel is far from the viewport — no slides are warm. */
const EMPTY_WARM: Set<number> = new Set();

/**
 * The autoplay plugin skips its own setup when the carousel resolves to a single
 * scroll snap — one slide, or a breakpoint where nothing overflows — but still
 * exposes `play`/`stop`, which then read internal state it never built and throw
 * (FIN-AI-5D8). Re-resolve per call: the snap count changes on every `reInit`.
 */
function playableAutoplay(emblaApi: EmblaCarouselType) {
  if (emblaApi.scrollSnapList().length <= 1) return undefined;
  return emblaApi.plugins()?.autoplay;
}

/** Root context provider. Plays when the carousel enters the viewport, stops when it leaves. */
export function Root({
  options,
  autoplayOptions,
  autoProgress = true,
  fade = true,
  priority = false,
  children,
  className,
}: RootProps) {
  // Plugins are created once per mount, so later autoplayOptions/autoProgress/fade
  // changes are ignored.
  const [plugins] = React.useState(() => {
    const fadePlugins = fade ? [FadePlugin()] : [];
    if (!autoProgress) return fadePlugins;

    const { delay: rootDelay = 3000, ...restAutoplayOptions } =
      autoplayOptions ?? {};
    return [
      AutoplayPlugin({
        playOnInit: false,
        delay: (scrollSnaps, emblaApi) =>
          scrollSnaps.map((_, i) => {
            const attr = emblaApi.slideNodes()[i]?.dataset?.autoplayDelay;
            return attr !== undefined ? parseInt(attr, 10) : rootDelay;
          }),
        stopOnInteraction: false,
        ...restAutoplayOptions,
      }),
      ...fadePlugins,
    ];
  });
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { watchDrag: false, ...options },
    plugins,
  );
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { warmIndices, setHoveredIndex } = useWarmIndices(emblaApi);

  // --- Preload / load gating ------------------------------------------------
  // A single "carousel is near the viewport" signal drives two things: slides
  // only become "warm" (eager image loading) and heavy slide media (Rive) only
  // instantiates once `shouldWarm` is true — so a far-down carousel no longer
  // eager-loads its first image OR fetches its `.riv` on initial page load.
  // When `priority` is set the carousel starts warm, so an above-the-fold first
  // slide gets `priority`/Rive immediately with no JS gating. The observer
  // never fires for a display:none element, so a CSS-hidden carousel (e.g. the
  // desktop variant on mobile) stays cold.
  const [shouldWarm, setShouldWarm] = React.useState(priority);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || shouldWarm) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldWarm(true);
          observer.disconnect();
        }
      },
      // Pre-warm ~half a viewport early so media is fetched just before it
      // scrolls into view, not only once it is fully visible.
      { rootMargin: "50% 0px" },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [shouldWarm]);

  // --- Viewport intersection (autoplay) -----------------------------------
  const autoplayInView = React.useRef(false);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || !emblaApi) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        autoplayInView.current = entry.isIntersecting;
        const autoplay = playableAutoplay(emblaApi);
        if (entry.isIntersecting) {
          autoplay?.play();
        } else {
          autoplay?.stop();
        }
      },
      { threshold: 0 },
    );

    const onReInit = () => {
      if (autoplayInView.current) {
        playableAutoplay(emblaApi)?.play();
      }
    };

    emblaApi.on("reInit", onReInit);
    observer.observe(container);
    return () => {
      emblaApi.off("reInit", onReInit);
      observer.disconnect();
    };
  }, [emblaApi]);

  return (
    <CarouselContext.Provider
      value={{
        emblaRef,
        emblaApi,
        autoProgress,
        isInView: shouldWarm,
        warmIndices: shouldWarm ? warmIndices : EMPTY_WARM,
        setHoveredIndex,
      }}
    >
      <div ref={containerRef} className={cn("relative", className)}>
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

/** The `overflow-hidden` wrapper that receives the Embla viewport ref. */
export function Viewport({ className, ...rest }: React.ComponentProps<"div">) {
  const { emblaRef } = useCarousel();
  return (
    <div
      ref={emblaRef}
      className={cn("overflow-hidden", className)}
      {...rest}
    />
  );
}

/** The flex container that holds slides. */
export function Container({ className, ...rest }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("flex touch-pan-y touch-pinch-zoom", className)}
      {...rest}
    />
  );
}

/** A single slide. Control width via `className`. Pass `delay` to override the root autoplay delay for this slide. */
export function Slide({
  className,
  delay,
  index,
  children,
  ...rest
}: SlideProps) {
  const { warmIndices } = useCarousel();
  const preload = index !== undefined && warmIndices.has(index);
  const slideCtx = React.useMemo(() => ({ preload }), [preload]);

  return (
    <div
      className={cn("min-w-0 shrink-0 grow-0", className)}
      data-autoplay-delay={delay}
      {...rest}
    >
      {index !== undefined ? (
        <SlideContext.Provider value={slideCtx}>
          {children}
        </SlideContext.Provider>
      ) : (
        children
      )}
    </div>
  );
}
